import React, { useContext, useEffect, useState } from 'react';

import { compose } from 'redux';

import { updateViewerTheme } from 'common/actions/viewer';
import AJAX from 'common/AJAX';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ThemeContext } from 'common/containers/ThemeContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import useMutation from 'common/hooks/useMutation';
import ThemeSelect from 'common/subdomain/admin/ThemeSelect';
import Flex from 'common/ui/Flex';
import Themes, { type Theme } from 'common/util/theme';

import type { Dispatch } from 'redux-connect';

type OwnProps = Record<string, never>;

type ConnectProps = {
  updateViewerTheme: (theme: Theme) => void;
  viewer: { theme?: Theme };
};

type Props = OwnProps & ConnectProps;

const AdminThemeSettings = ({ updateViewerTheme, viewer }: Props) => {
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);
  const showToast = useContext(ShowToastContext);
  const themeContext = useContext(ThemeContext);

  const [theme, setTheme] = useState<Theme>(viewer?.theme || Themes.auto);

  useEffect(() => {
    if (location.query?.success) {
      router.replace({
        pathname: location.pathname,
        query: undefined,
      });
      showToast('Theme preference updated', ToastTypes.success);
    }
  }, [location.pathname, location.query, router, showToast]);

  const updateTheme = useMutation({
    mutationFn: async (updatedTheme: Theme) => {
      setTheme(updatedTheme);
      return AJAX.post('/api/viewer/setTheme', { theme: updatedTheme });
    },
    isResponseSuccessful: (response) => response === 'success',
    onSuccess: (_, updatedTheme) => {
      updateViewerTheme(updatedTheme);
      if (themeContext) {
        if (updatedTheme === Themes.auto) {
          const browserTheme =
            window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
              ? Themes.dark
              : Themes.light;
          themeContext.setTheme(browserTheme);
        } else {
          themeContext.setTheme(updatedTheme);
        }
      }
    },
    onError: () => {
      showToast('Failed to update theme preference', ToastTypes.error);
      setTheme(viewer?.theme || Themes.auto);
    },
  });

  return (
    <Flex className="items-center justify-between max-w-[720px]">
      <Helmet title="Theme Preferences | Canny" />
      <div>
        <h2 className="mb-1.5 font-bold text-base">Theme</h2>
        <p>Select your preferred theme for the admin interface.</p>
      </div>
      <ThemeSelect value={theme} onValueChange={updateTheme.mutate} />
    </Flex>
  );
};

export default compose(
  connect(
    ({ viewer }: { viewer: { theme?: Theme } }) => ({
      viewer,
    }),
    (dispatch: Dispatch) => ({
      updateViewerTheme: (theme: Theme) => dispatch(updateViewerTheme(theme)),
    }),
  ),
)(AdminThemeSettings) as unknown as React.FC<OwnProps>;
